/**
 * QualityManager - Applies quality presets to all rendering systems
 *
 * The store's `quality` slice is the single source of truth for the active
 * QualitySettings. `applyQuality` writes that slice and pushes the settings to
 * the subsystems it owns imperatively (renderer, lighting, terrain/water
 * materials, visibility). The store-subscription-driven subsystems react on
 * their own: effects.ts (ssao/bloom/godrays/msaa/color-correction) and SkyDome
 * (god-ray sun disc) subscribe to `quality`.
 *
 * Individual `apply*()` helpers (used by the debug panel) update the `quality`
 * slice AND apply their field immediately.
 */

import * as THREE from 'three';
import {
  QualityLevel,
  isLowTier,
  QualitySettings,
  QUALITY_PRESETS,
  snapViewDistance,
  snapFarLodLevels,
  saveQualityLevel,
  saveFarLodLevels,
} from './QualityPresets.js';
import { getActiveShadowLight, setMoonShadowsAllowed, updateShadowFrustumSize, applyEnvironmentSettings } from '../scene/Lighting.js';
import {
  setShaderMapDefines,
  setTerrainAnisotropy,
  setShadowCasterDetail,
} from '../material/TerrainMaterial.js';
import { setWaterQualityLow } from '../material/WaterMaterial.js';
import { setGodRaysSamples } from '../render/PostPipeline.js';
import { useGameStore } from '../../state/store.js';

// ============== Managed References ==============

/** Set by GameCore.init() */
let rendererRef: THREE.WebGLRenderer | null = null;

/** Callback set by VoxelWorld or GameCore to update visibility radius at runtime */
let visibilityRadiusCallback: ((radius: number) => void) | null = null;

/** Callback set by GameCore to update the far-LOD ring count at runtime */
let farLodLevelsCallback: ((levels: number) => void) | null = null;

// ============== Public API ==============

export function setRendererRef(r: THREE.WebGLRenderer): void {
  rendererRef = r;
}

export function getRendererRef(): THREE.WebGLRenderer | null {
  return rendererRef;
}

export function setVisibilityRadiusCallback(cb: (radius: number) => void): void {
  visibilityRadiusCallback = cb;
}

export function setFarLodLevelsCallback(cb: (levels: number) => void): void {
  farLodLevelsCallback = cb;
}

// ============== Internal imperative appliers (no store writes) ==============

function shadowsImpl(enabled: boolean): void {
  if (rendererRef) {
    rendererRef.shadowMap.enabled = enabled;
    rendererRef.shadowMap.needsUpdate = true;
  }
  const light = getActiveShadowLight();
  if (light) light.castShadow = enabled;
}

function shadowMapSizeImpl(size: number): void {
  // shadowMapSize still lives on the environment slice (the applied home);
  // the quality preset seeds it here.
  useGameStore.getState().setEnvironment({ shadowMapSize: size });
  applyEnvironmentSettings({ shadowMapSize: size });
}

function visibilityImpl(radius: number): void {
  if (visibilityRadiusCallback) visibilityRadiusCallback(radius);
}

function farLodLevelsImpl(levels: number): void {
  if (farLodLevelsCallback) farLodLevelsCallback(levels);
}

// ============== Preset application ==============

/**
 * Apply a full quality preset. A preset decides LOOKS only: the view distance and the far rings are
 * whatever they are now unless the caller passes them (boot does; a preset switch never does).
 */
export function applyQuality(level: QualityLevel, customVisibility?: number, customFarLods?: number): void {
  const preset = QUALITY_PRESETS[level];
  const live = useGameStore.getState().quality;
  const settings: QualitySettings = {
    ...preset,
    // Snap a persisted custom radius (possibly from an older 2/4/6/8 build) into the current set.
    visibilityRadius: customVisibility !== undefined ? snapViewDistance(customVisibility) : live.visibilityRadius,
    farLodLevels: customFarLods !== undefined ? snapFarLodLevels(customFarLods) : live.farLodLevels,
  };

  // Single source of truth — this also drives the effects.ts / SkyDome
  // subscriptions (ssao/bloom/godrays/msaa/color-correction, sun disc).
  useGameStore.getState().setQuality(settings);

  // Subsystems this manager owns imperatively:
  shadowsImpl(settings.shadowsEnabled);
  setMoonShadowsAllowed(settings.shadowsEnabled); // moon casts whenever shadows are on (unified with sun)
  shadowMapSizeImpl(settings.shadowMapSize);
  // Foliage casts as solid cards on the low tier: no alpha test, no wind, in the caster pass.
  setShadowCasterDetail(!isLowTier(level));
  updateShadowFrustumSize(settings.shadowRadius);
  setShaderMapDefines({
    normalMaps: settings.shaderNormalMaps,
    aoMaps: settings.shaderAoMaps,
    metalnessMaps: settings.shaderMetalnessMaps,
  });
  setTerrainAnisotropy(settings.anisotropy);
  setWaterQualityLow(!settings.waterHighQuality);
  setGodRaysSamples(settings.godRaysSamples);
  visibilityImpl(settings.visibilityRadius);
  farLodLevelsImpl(settings.farLodLevels);

  // Persist. NOT the view distance: every session starts at START_VIEW_DISTANCE by design, so
  // remembering it would only write a key nothing reads.
  saveQualityLevel(level);
  if (customFarLods !== undefined) {
    saveFarLodLevels(customFarLods);
  }

  console.log(`[Quality] Applied preset: ${level}`, {
    shadows: settings.shadowsEnabled,
    shadowMap: settings.shadowMapSize,
    shadowRadius: settings.shadowRadius,
    ssao: settings.ssaoEnabled,
    bloom: settings.bloomEnabled,
    godRays: settings.godRaysEnabled,
    godRaysSamples: settings.godRaysSamples,
    visibility: settings.visibilityRadius,
    anisotropy: settings.anisotropy,
  });
}

/**
 * Apply a quality preset and record the selected level. Single entry-point that
 * UI buttons and GameCore.init should call.
 */
export function syncQualityToStore(level: QualityLevel, customVisibility?: number, customFarLods?: number): void {
  applyQuality(level, customVisibility, customFarLods);
  useGameStore.getState().setQualityLevel(level);
}

// ============== Quality patch applier (segmented Quality UI) ==============

/**
 * Apply a partial `QualitySettings` change (one segment of the Quality UI) — updates the
 * store slice, then routes each changed key to its imperative subsystem. Post-processing
 * enables (ssao/bloom/godRaysEnabled) apply themselves via the effects.ts store
 * subscription, so they only need the store write. This single function replaces the old
 * per-field `applyX` appliers.
 */
export function applyQualityPatch(patch: Partial<QualitySettings>): void {
  useGameStore.getState().updateQuality(patch);

  if (patch.shadowsEnabled !== undefined) {
    shadowsImpl(patch.shadowsEnabled);
    setMoonShadowsAllowed(patch.shadowsEnabled); // moon casts whenever shadows are on
  }
  if (patch.shadowMapSize !== undefined) shadowMapSizeImpl(patch.shadowMapSize);
  if (patch.shadowRadius !== undefined) updateShadowFrustumSize(patch.shadowRadius);
  if (patch.anisotropy !== undefined) setTerrainAnisotropy(patch.anisotropy);
  if (patch.waterHighQuality !== undefined) setWaterQualityLow(!patch.waterHighQuality);
  if (patch.godRaysSamples !== undefined) setGodRaysSamples(patch.godRaysSamples);
  if (
    patch.shaderNormalMaps !== undefined
    || patch.shaderAoMaps !== undefined
    || patch.shaderMetalnessMaps !== undefined
  ) {
    const q = useGameStore.getState().quality;
    setShaderMapDefines({
      normalMaps: q.shaderNormalMaps,
      aoMaps: q.shaderAoMaps,
      metalnessMaps: q.shaderMetalnessMaps,
    });
  }
  if (patch.visibilityRadius !== undefined) {
    visibilityImpl(patch.visibilityRadius);
  }
  if (patch.farLodLevels !== undefined) {
    farLodLevelsImpl(patch.farLodLevels);
    saveFarLodLevels(patch.farLodLevels);
  }
}

/** Apply just the visibility radius (thin wrapper — home screen View control). */
export function applyVisibilityRadius(radius: number): void {
  applyQualityPatch({ visibilityRadius: snapViewDistance(radius) });
}

/**
 * Apply just the far-view ring count (thin wrapper — home screen Far View control). Snapped to the
 * offered options (Off / 2 / 4 / 8 RINGS) rather than clamped to a literal, so the value always
 * matches a pill and the ceiling lives in FAR_VIEW_OPTIONS.
 */
export function applyFarLodLevels(levels: number): void {
  applyQualityPatch({ farLodLevels: snapFarLodLevels(Math.floor(levels)) });
}

/** Current shadow radius in chunks. Used by VoxelWorld for castShadow culling. */
export function getShadowRadius(): number {
  return useGameStore.getState().quality.shadowRadius;
}

export function getCurrentSettings(): QualitySettings {
  return useGameStore.getState().quality;
}
